import { GiftPartner } from '@shared/api/giftPartner/giftPartner.interface';
import { Button } from 'antd';
import { Link } from 'react-router-dom';

import { GIFT_PARTNER_GUIDE_PATH } from './guide/giftPartnerGuideLinks';

/** Show the next applicant action from server state, including approval before issuance. */
export const GiftPartnerApplicantGuide = ({
  partner,
  busy = false,
  onIssue,
}: {
  partner?: GiftPartner;
  busy?: boolean;
  onIssue?: () => void;
}) => {
  if (
    partner?.status === 'Rejected' ||
    (partner?.status === 'Approved' && !partner.isEnabled)
  )
    return null;
  const keys = partner?.apiKeys.filter((key) => !key.revokedAt) ?? [];
  const approved = partner?.status === 'Approved';
  const called = approved && keys.some((key) => !!key.lastUsedAt);
  const complete = [!!partner, keys.length > 0, !!approved, !!called];
  const current = complete.findIndex((done) => !done);
  const title = !partner
    ? '먼저 연동을 신청하고, 다음 화면에서 API 키를 발급받으세요'
    : !keys.length
    ? 'API 키를 발급받아 개발 담당자에게 전달하세요'
    : !approved
    ? '키 발급 완료 · 관리자 승인을 기다리고 있습니다'
    : !called
    ? '승인 완료 · 발급받은 키로 서버 연결을 확인하세요'
    : 'API 호출이 확인되었습니다';
  const description = !partner
    ? '연동 신청은 어떤 브랜드의 발주를 어느 서버로 보낼지 등록하는 절차입니다. API 키는 등록한 서버가 발주를 조회할 때 사용하는 인증 정보이며, 신청 후 직접 발급받습니다.'
    : !keys.length
    ? approved
      ? '신청은 승인되었습니다. 키를 발급받으면 등록한 서버 IP에서 바로 API를 호출할 수 있습니다.'
      : '연동 신청이 접수되었습니다. 승인 전에도 키를 미리 발급해 서버에 설정할 수 있습니다. 실제 발주 조회와 배송 처리는 관리자 승인 후 가능합니다.'
    : !approved
    ? '지금은 발급받은 키를 서버에 설정해 두세요. 승인 전 API 호출은 거부됩니다(401). 승인되면 같은 키를 그대로 사용하므로 다시 발급받을 필요가 없습니다.'
    : !called
    ? '개발 담당자가 등록한 서버 IP에서 GET /partner/v1/me를 호출하면 됩니다. 키를 다시 발급받을 필요는 없습니다. 웹훅 알림은 필요한 경우 별도로 설정하세요.'
    : '이 연동의 키로 인증한 기록이 있습니다. 발주 조회·배송 상태 변경과 필요한 웹훅 수신을 개발 가이드에 따라 확인하세요.';
  return (
    <section className="gift-partner-onboarding" aria-label="연동 시작 안내">
      <ol className="gift-partner-steps" aria-label="연동 진행 단계">
        {['연동 신청', 'API 키 발급', '관리자 승인', '서버 연결 확인'].map(
          (label, index) => (
            <li
              key={label}
              aria-current={current === index ? 'step' : undefined}
              data-complete={complete[index]}
            >
              <span className="gift-partner-step-number" aria-hidden="true">
                {complete[index] ? '✓' : index + 1}
              </span>
              <span>
                {label}
                <small>
                  {complete[index] ? '완료' : current === index ? '현재 단계' : '예정'}
                </small>
              </span>
            </li>
          ),
        )}
      </ol>
      <div className="gift-partner-next-action">
        <h2>{title}</h2>
        <p>{description}</p>
        {!partner ? (
          <div className="gift-partner-preparation">
            <strong>신청 전에 준비해 주세요</strong>
            <span>받을 브랜드 · 호출 서버의 고정 공인 IP · 개발 담당자 연락처</span>
            <span>
              서버 정보를 모르시면 개발 담당자에게 아래 가이드를 전달해 주세요. 알림을
              받을 콜백 URL은 나중에 등록해도 됩니다.
            </span>
          </div>
        ) : !keys.length && onIssue ? (
          <Button
            type="primary"
            className="gift-partner-primary"
            disabled={busy}
            onClick={onIssue}
          >
            API 키 발급받기
          </Button>
        ) : null}
        <Link
          className="gift-partner-next-link"
          to={`${GIFT_PARTNER_GUIDE_PATH}${approved && keys.length ? '#auth' : ''}`}
        >
          {approved && keys.length
            ? '서버 연결 확인 방법 보기 →'
            : '개발 담당자에게 전달할 연동 가이드 →'}
        </Link>
      </div>
    </section>
  );
};
